/**
 * fieldHelp.ts — zentrales Repository für Feld-Hilfetexte.
 *
 * Jeder Eintrag ist ein Tripel aus
 *   - what  (Was wird gefragt?)
 *   - why   (Warum brauchen wir das?)
 *   - example (Konkretes Beispiel)
 *
 * Die Texte werden vom <FieldHelp>-Tooltip gerendert. Sie sind Sie-Form,
 * behördentauglich und enthalten dort, wo Daten besonders sensibel sind,
 * einen DSGVO-Verweis (z.B. Gesundheitsfelder).
 *
 * Hinweis zur Pflege: Wenn ein neues Feld einen Hilfetext braucht,
 *   1. Pfad-Schlüssel hier ergänzen (siehe Schema unten)
 *   2. Im Renderer per `<FieldHelp pathKey="…" />` aufrufen
 *
 * Wir verwenden bewusst flache String-Schlüssel statt eines verschachtelten
 * Objekts, damit Verlinkungen aus Komponenten nicht typo-anfällig werden
 * (TypeScript fängt unbekannte Keys über die `FieldHelpKey`-Union).
 */

export interface FieldHelpEntry {
  /**
   * Menschen-lesbarer Label des Feldes (z.B. „Umsatz / Monat").
   * Wird in der Cockpit-Rückfragen-Inbox als „Zum Feld:" angezeigt — damit
   * der Berater das Feld auch ohne tieferes Code-Wissen wiederfindet.
   * Phase B: zentral hier gepflegt statt redundant an jedem `<FieldHelp>`-
   * Aufruf.
   */
  readonly label: string;
  /** Worum geht es? — kurze Erklärung in 1-2 Sätzen. */
  readonly what: string;
  /** Warum brauchen wir das? — Bezug zur Tragfähigkeitsprüfung. */
  readonly why: string;
  /** Beispiel — konkrete, alltagsnahe Veranschaulichung. */
  readonly example: string;
  /** Optional: zusätzlicher DSGVO-Hinweis (z.B. bei Gesundheitsdaten). */
  readonly dsgvoNote?: string;
  /**
   * Optional: Wizard-Step-Slug für die Cockpit-FieldPicker-Gruppierung.
   * Beispiele: 'stammdaten', 'triage', 'identity', 'activity', 'style',
   * 'resources', 'health', 'private'.
   * Default-Heuristik: erstes Pfad-Segment des FieldHelpKey.
   */
  readonly wizardStep?: string;
}

/**
 * Schlüssel sind sprechend nach Datenmodell benannt:
 *   `<dimension>.<feld>` oder `<bereich>.<feld>`.
 *
 * Reihenfolge im Object = thematische Gruppierung (BWA → BG → Stunden →
 * Identität → Aktivität → Stil → Gesundheit → Privatleben).
 */
export const FIELD_HELP = {
  // ─── Modul 05: Ressourcen & Finanzen ────────────────────────────────────────
  'resources.bwa.revenue': {
    label: 'Monatlicher Umsatz (netto)',
    wizardStep: 'resources',
    what: 'Ihr durchschnittlicher monatlicher Umsatz aus Ihrer selbständigen Tätigkeit, ohne Umsatzsteuer.',
    why: 'Der Umsatz ist die wichtigste Eingangsgröße zur Bewertung Ihrer Ertragslage. Aus dem Umsatz minus Kosten ergibt sich der Gewinn, der nach § 11 SGB II auf Ihren Bedarf angerechnet wird.',
    example:
      'Wenn Sie in den letzten sechs Monaten zwischen 4.000 und 5.000 Euro netto Umsatz erzielt haben, tragen Sie als Mittelwert 4.500 Euro ein.',
  },
  'resources.bwa.costs': {
    label: 'Monatliche Betriebsausgaben',
    wizardStep: 'resources',
    what: 'Ihre durchschnittlichen monatlichen Betriebsausgaben, ohne private Entnahmen.',
    why: 'Betriebsausgaben mindern Ihren steuerlichen Gewinn und damit das anrechenbare Einkommen nach § 11b SGB II. Wir brauchen den Wert, um Ihren Gewinn zu errechnen.',
    example:
      'Miete für Geschäftsräume, Material, Fahrtkosten, Versicherungen, Steuerberatung. Beispiel: 1.800 Euro im Monat.',
  },
  'resources.bwa.profit': {
    label: 'Gewinn (Umsatz minus Kosten)',
    wizardStep: 'resources',
    what: 'Der Gewinn ist Umsatz minus Betriebsausgaben — wir errechnen ihn automatisch aus Ihren beiden Eingaben.',
    why: 'Der Gewinn ist die Grundlage für die Beurteilung, ob Ihre Tätigkeit Sie und Ihre Bedarfsgemeinschaft tragen kann. Er fließt in mehrere Kennzahlen ein (Stundensatz, Tragfähigkeitslücke).',
    example:
      'Bei 4.500 Euro Umsatz und 1.800 Euro Kosten ergibt sich ein Gewinn von 2.700 Euro im Monat.',
  },
  'resources.rate.hours': {
    label: 'Abrechenbare Stunden pro Monat',
    wizardStep: 'resources',
    what: 'Die Stunden pro Monat, in denen Sie tatsächlich bezahlte Arbeitsleistung erbringen.',
    why: 'Daraus errechnen wir Ihren bereinigten Stundensatz — eine zentrale Kennzahl. Liegt er deutlich unter dem branchenüblichen Wert, gilt Ihre Tätigkeit als nicht tragfähig.',
    example:
      'Bei 35 Stunden pro Woche an reinen Kundenaufträgen sind das ca. 140 abrechenbare Stunden im Monat. Akquise, Buchhaltung und Krankheitstage zählen NICHT als abrechenbar.',
  },
  'resources.rate.bereinigt': {
    label: 'Bereinigter Stundensatz',
    wizardStep: 'resources',
    what: 'Ihr bereinigter Stundensatz: Gewinn geteilt durch abrechenbare Stunden pro Monat.',
    why: 'Diese Zahl zeigt, ob Sie pro Arbeitsstunde wirtschaftlich tragfähig arbeiten. Wir vergleichen sie mit der branchenüblichen Untergrenze (typisch 25 - 35 Euro).',
    example:
      'Bei 2.700 Euro Gewinn und 140 Stunden ergibt sich ein bereinigter Stundensatz von 19,29 Euro — das wäre zu niedrig für die meisten Branchen.',
  },
  'resources.liquidity.assets': {
    label: 'Liquide Mittel zum Stichtag',
    wizardStep: 'resources',
    what: 'Die Summe aller Mittel, die Sie kurzfristig (innerhalb weniger Tage) verfügbar machen können.',
    why: 'Liquide Mittel zeigen, wie viele Monate Sie überstehen, falls Aufträge ausbleiben. Die Reichweite (Liquidität geteilt durch monatliche Kosten) ist eine Frühwarn-Kennzahl.',
    example:
      'Geschäftskonto 1.500 Euro, Tagesgeld-Rücklage 3.000 Euro = 4.500 Euro liquide Mittel. Sachwerte (Auto, Werkzeug, Lagerbestand) zählen NICHT mit.',
    dsgvoNote:
      'Wir erfragen nur die Summe, keinen Bankauszug. Ihre Beraterin oder Ihr Berater kann später Belege einsehen, aber nur mit Ihrer Zustimmung.',
  },
  'resources.bg.composition': {
    label: 'Bedarfsgemeinschaft — Zusammensetzung',
    wizardStep: 'resources',
    what: 'Alle Personen, die in einem gemeinsamen Haushalt mit Ihnen leben und für die ein gemeinsamer Bedarf nach SGB II gilt.',
    why: 'Die Bedarfsgemeinschaft (BG) bestimmt, wie viel Geld Ihr Haushalt monatlich benötigt. Aus diesem Bedarf und Ihrem Gewinn errechnen wir die Tragfähigkeitslücke.',
    example:
      'Sie selbst, Ihre Partnerin oder Ihr Partner und ein achtjähriges Kind = drei Personen in der BG. WG-Mitbewohner zählen NICHT zur BG, eine eheähnliche Lebensgemeinschaft schon.',
  },
  'resources.bg.kdu': {
    label: 'Kosten der Unterkunft (KdU)',
    wizardStep: 'resources',
    what: 'Die Kosten der Unterkunft (KdU): Kaltmiete + kalte Nebenkosten + Heizkosten Ihrer Wohnung.',
    why: 'KdU werden nach § 22 SGB II zusätzlich zum Regelbedarf gezahlt — sofern angemessen. Die Höhe fließt direkt in den errechneten Bedarf Ihrer BG ein.',
    example:
      '600 Euro Kaltmiete + 120 Euro Nebenkosten + 80 Euro Heizung = 800 Euro KdU pro Monat. Strom rechnet NICHT zur KdU (ist im Regelbedarf enthalten).',
  },
  'resources.bg.children': {
    label: 'Kinder unter 15 Jahren',
    wizardStep: 'resources',
    what: 'Anzahl und Alter Ihrer Kinder unter 15 Jahren, für die Sie betreuungspflichtig sind.',
    why: 'Kinder unter 15 lösen einen erhöhten Bedarf nach SGB II aus (Regelsatz, ggf. Mehrbedarfe für Alleinerziehende nach § 21 Abs. 3) und können Ihre verfügbare Arbeitszeit begrenzen.',
    example:
      'Zwei Kinder, sechs und neun Jahre alt. Bei alleinerziehender Erziehung greift § 21 Abs. 3 SGB II zusätzlich.',
  },

  // ─── Modul 02: Identität & Qualifikation ────────────────────────────────────
  'identity.qualification': {
    label: 'Berufliche Qualifikation',
    wizardStep: 'identity',
    what: 'Ihre formale Ausbildung, relevante Weiterbildungen und Berufsjahre in dem Feld, in dem Sie selbständig arbeiten.',
    why: 'Die fachliche Eignung ist Grundvoraussetzung für eine tragfähige Selbständigkeit. Bei reglementierten Berufen (Handwerksmeister, Heilberufe) ist sie sogar gesetzlich vorgeschrieben.',
    example:
      'Ausbildung als Steuerfachangestellte (2010), 8 Jahre in einer Kanzlei, seit 2024 selbständig in eigener Praxis. Bei Handwerk: Meisterbrief (Datum, Kammer).',
  },

  // ─── Modul 03: Aktivität & Geschäftsmodell ──────────────────────────────────
  'activity.offering': {
    label: 'Beschreibung des Angebots',
    wizardStep: 'activity',
    what: 'Eine kurze, sachliche Beschreibung Ihres Angebots: was Sie verkaufen, an welche Kundengruppe, mit welchem Erlösmodell.',
    why: 'Aus dem Geschäftsmodell ergibt sich die Plausibilität Ihrer Umsatzplanung. Vage Beschreibungen („Coaching", „Beratung") werden vom Jobcenter regelmäßig hinterfragt.',
    example:
      'Buchhaltung und Lohnabrechnung für kleine Handwerksbetriebe in München und Umgebung. Pauschalpreis pro Mandat (60 - 200 Euro im Monat). Aktuell 12 Mandate.',
  },
  'activity.branch': {
    label: 'Branche',
    wizardStep: 'activity',
    what: 'Die Branche, in der Sie tätig sind — möglichst nach gängiger Klassifikation.',
    why: 'Die Branche bestimmt branchenübliche Stundensätze und Vergleichswerte für die Bewertung. Auch das Jobcenter ordnet Sie nach Branche zu.',
    example:
      'Buchhaltungsservice, Bauhandwerk, IT-Beratung, Friseurhandwerk, Pflegedienst. Zur Orientierung: Klassifikation der Wirtschaftszweige (WZ 2008) der Bundesagentur.',
  },
  'activity.legalForm': {
    label: 'Rechtsform',
    wizardStep: 'activity',
    what: 'Ihre Rechtsform: Einzelunternehmen, GbR, UG, GmbH, Freiberufler oder Kleinunternehmer nach § 19 UStG.',
    why: 'Die Rechtsform beeinflusst Haftungsfragen, Buchhaltungspflichten und steuerliche Behandlung. Für die Tragfähigkeitsprüfung wichtig: Ist Ihr Privatvermögen geschützt?',
    example:
      'Einzelunternehmen mit Kleinunternehmerregelung, oder Freiberuflerin nach § 18 EStG, oder UG (haftungsbeschränkt) mit 1.000 Euro Stammkapital.',
  },

  // ─── Modul 04: Stil & Marktposition ─────────────────────────────────────────
  'style.positioning': {
    label: 'Marktposition / Differenzierung',
    wizardStep: 'style',
    what: 'Eine nüchterne, nicht-werbliche Beschreibung dessen, was Sie von Ihrem Wettbewerb unterscheidet.',
    why: 'Marktposition ist ein wesentlicher Indikator für nachhaltige Auftragslage. Wer austauschbar ist, gewinnt Kunden meist nur über den Preis — und das ist selten tragfähig.',
    example:
      'Persönliche Betreuung, kurze Reaktionszeit (Anrufe innerhalb von 4 Stunden zurück), Vor-Ort-Termine bei Mandanten ohne Aufpreis. Spezialisierung auf KFZ-Werkstätten.',
  },

  // ─── Modul 06: Gesundheit & Resilienz ───────────────────────────────────────
  'health.constraints': {
    label: 'Gesundheitliche Einschränkungen',
    wizardStep: 'health',
    what: 'Gesundheitliche oder psychische Einschränkungen, die Ihre Arbeitsfähigkeit dauerhaft begrenzen.',
    why: 'Wir brauchen die Information nur dort, wo sie für die Beurteilung der Tragfähigkeit relevant ist (z.B. weil Sie nicht mehr 40 Stunden arbeiten können).',
    example:
      'Chronische Rückenbeschwerden begrenzen die Arbeitszeit auf ca. 30 Stunden pro Woche. Oder: GdB 50, anerkannte Behinderung.',
    dsgvoNote:
      'Diese Angaben sind besonders sensible Daten nach Art. 9 DSGVO. Bitte nennen Sie nur, was für die Tragfähigkeit relevant ist — keine Diagnosen oder Befunde.',
  },

  // ─── Modul 00: Stammdaten ────────────────────────────────────────────────────
  'stammdaten.fullName': {
    label: 'Name (Vor- und Nachname)',
    wizardStep: 'stammdaten',
    what: 'Ihr vollständiger amtlicher Vor- und Nachname.',
    why: 'Der Name wird in der formellen Stellungnahme an das Jobcenter und in allen Beweisdokumenten verwendet.',
    example: 'Maria Mustermann (so wie im Personalausweis).',
    dsgvoNote:
      'Ihr Name wird ausschließlich für die Stellungnahme verwendet und nicht zu Marketingzwecken verarbeitet.',
  },
  'stammdaten.birthdate': {
    label: 'Geburtsdatum',
    wizardStep: 'stammdaten',
    what: 'Ihr Geburtsdatum im Format TT.MM.JJJJ.',
    why: 'Bestimmte Mehrbedarfe nach SGB II hängen vom Alter ab. Außerdem identifiziert das Datum Sie eindeutig in Behörden-Vorgängen.',
    example: '14.05.1980',
  },
  'stammdaten.address': {
    label: 'Anschrift',
    wizardStep: 'stammdaten',
    what: 'Ihre aktuelle Wohnanschrift mit Straße, Hausnummer, Postleitzahl und Stadt.',
    why: 'Die Anschrift wird im Stellungnahme-Briefkopf verwendet. Außerdem prüft das Jobcenter darüber Ihre Zuständigkeit.',
    example: 'Hauptstraße 12\n80331 München',
  },
  'stammdaten.branch': {
    label: 'Branche',
    wizardStep: 'stammdaten',
    what: 'Die Branche, in der Sie selbständig tätig sind.',
    why: 'Aus der Branche leiten wir branchenübliche Vergleichswerte (z.B. Mindeststundensätze) für die Bewertung ab.',
    example:
      'Wenn Sie ein Bauhandwerk betreiben, wählen Sie „Handwerk". Bei Buchhaltung wählen Sie „Beratung".',
  },
  'stammdaten.legalForm': {
    label: 'Rechtsform',
    wizardStep: 'stammdaten',
    what: 'Die Rechtsform, in der Sie wirtschaftlich tätig sind.',
    why: 'Die Rechtsform beeinflusst Haftungsfragen, Buchhaltungspflichten und steuerliche Behandlung — wichtig für die Tragfähigkeit.',
    example: 'Einzelunternehmen oder Freiberufler nach § 18 EStG sind die häufigsten Formen.',
  },
  'stammdaten.startDate': {
    label: 'Beginn der Selbständigkeit',
    wizardStep: 'stammdaten',
    what: 'Datum, an dem Sie Ihre Selbständigkeit angemeldet haben (Gewerbeanmeldung oder Aufnahme einer freiberuflichen Tätigkeit).',
    why: 'Die Dauer Ihrer Selbständigkeit ist ein zentraler Indikator: nach einem Jahr greift die § 10 Abs. 2 Nr. 5 SGB II-Prüfung.',
    example: '01.04.2024',
  },
  'stammdaten.taxId': {
    label: 'Steuernummer (Finanzamt)',
    wizardStep: 'stammdaten',
    what: 'Ihre Steuernummer beim zuständigen Finanzamt — NICHT die persönliche Steuer-Identifikationsnummer.',
    why: 'Die Steuernummer ist optional, hilft aber dem Jobcenter bei Rückfragen mit dem Finanzamt.',
    example: '21/123/45678 (Format variiert je nach Bundesland)',
    dsgvoNote:
      'Sie müssen die Steuernummer nicht angeben — sie ist optional. Wenn Sie sie eintragen, wird sie ausschließlich für die Stellungnahme verwendet.',
  },
  'stammdaten.description': {
    label: 'Kurzbeschreibung der Tätigkeit',
    wizardStep: 'stammdaten',
    what: 'Ein bis zwei Sätze, die Ihre Tätigkeit klar beschreiben.',
    why: 'Die Beschreibung wird im ersten Absatz der Stellungnahme zitiert. Sie sollte für eine Sachbearbeiterin auf Anhieb verständlich sein.',
    example:
      'Buchhaltung und Lohnabrechnung für kleine Handwerksbetriebe in München. Pauschalpreis pro Mandat (60–200 Euro im Monat).',
  },

  // ─── Modul 07: Privates Umfeld ──────────────────────────────────────────────
  'private.care': {
    label: 'Pflegeverpflichtungen',
    wizardStep: 'private',
    what: 'Pflegebedürftige Angehörige, für die Sie regelmäßig Zeit aufwenden.',
    why: 'Pflegeverpflichtungen können Ihre verfügbare Arbeitszeit erheblich begrenzen und sind ein wichtiger Kontextfaktor. Sie können auch Mehrbedarfe nach SGB II auslösen.',
    example:
      'Mutter mit Pflegegrad 3, lebt im Nachbarort, ich übernehme ca. 8 Stunden Pflege pro Woche. Oder: keine Pflegeverpflichtungen.',
    dsgvoNote:
      'Sie müssen weder Namen noch Diagnosen Ihrer Angehörigen nennen. Eine grobe Beschreibung reicht.',
  },
} as const;

export type FieldHelpKey = keyof typeof FIELD_HELP;

/**
 * Liefert den Hilfetext zu einem Schlüssel, oder undefined wenn keiner
 * existiert. Komponenten, die einen unbekannten Schlüssel anfragen, sollten
 * still nichts rendern (defensive: kein Crash).
 */
export function getFieldHelp(key: string): FieldHelpEntry | undefined {
  return (FIELD_HELP as Readonly<Record<string, FieldHelpEntry>>)[key];
}
